import React from 'react';
import { Clock, Loader2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import type { JobStatus } from '../types';

export interface StatusConfigEntry {
  label: string;
  variant: 'warning' | 'info' | 'success' | 'danger';
  icon: React.ReactNode;
  message: string;
}

export const statusConfig: Record<JobStatus, StatusConfigEntry> = {
  pending: {
    label: 'Pending',
    variant: 'warning',
    icon: React.createElement(Clock, { className: 'h-3.5 w-3.5' }),
    message: 'Your blog is queued for generation...',
  },
  processing: {
    label: 'Processing',
    variant: 'info',
    icon: React.createElement(Loader2, { className: 'h-3.5 w-3.5 animate-spin' }),
    message: 'AI is crafting your blog content...',
  },
  completed: {
    label: 'Completed',
    variant: 'success',
    icon: React.createElement(CheckCircle, { className: 'h-3.5 w-3.5' }),
    message: 'Your blog is ready!',
  },
  failed: {
    label: 'Failed',
    variant: 'danger',
    icon: React.createElement(AlertCircle, { className: 'h-3.5 w-3.5' }),
    message: 'Something went wrong during generation.',
  },
  retrying: {
    label: 'Retrying',
    variant: 'warning',
    icon: React.createElement(RefreshCw, { className: 'h-3.5 w-3.5 animate-spin' }),
    message: 'Retrying generation...',
  },
};

// Handles dynamic "retrying (1/3)" strings from backend
export const getStatusConfig = (status: string): StatusConfigEntry => {
  if (status.startsWith('retrying')) {
    return {
      label: status,
      variant: 'warning',
      icon: React.createElement(RefreshCw, { className: 'h-3.5 w-3.5 animate-spin' }),
      message: 'Retrying generation...',
    };
  }
  return statusConfig[status as JobStatus] ?? statusConfig['pending'];
};

/**
 * Human-readable explanation for a machine-readable failure code.
 *
 * Job errors are otherwise raw strings from the backend; a known code lets the
 * UI say what actually went wrong and where to fix it.
 */
export const ERROR_CODE_MESSAGES: Record<string, string> = {
  claude_auth_expired:
    'The Claude connection has expired. An administrator can restore it in Settings → Claude Connection.',
};

export const getErrorMessage = (
  error: string | null | undefined,
  errorCode?: string | null
): string | null => {
  if (errorCode && ERROR_CODE_MESSAGES[errorCode]) {
    return ERROR_CODE_MESSAGES[errorCode];
  }
  return error ?? null;
};
